// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

// Import Material UI
import { Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import Close from '@material-ui/icons/Close';

// Import Pages
import ModuleItem from '../../../leftArea/ModuleItem/ModuleItem';


// Import Recoil
import { activeAutom, automModules, scopeLibraryAtom } from '../../../../../../../recoil-store/recoil-state';

// Import Icons
import ApplicationIcon from '../../../../../../../assets/icons/moduleIcons/applications-icon.svg';
import AIicon from '../../../../../../../assets/icons/moduleIcons/ai-actions-icon.svg';
import UIicon from '../../../../../../../assets/icons/moduleIcons/ui-actions-icon.svg';
import AdvancedActionIcon from '../../../../../../../assets/icons/moduleIcons/advanced-actions-icon.svg';
import SearchIcon from '../../../../../../../assets/icons/search-icon.svg';
import IntegrationIcon from '../../../../../../../assets/icons/moduleIcons/Integration.svg';
import TransformIcon from '../../../../../../../assets/icons/moduleIcons/Transform.svg';
import UtilityIcon from '../../../../../../../assets/icons/moduleIcons/Utility.svg';

// Import Settings
import { BASE_ORIGIN } from '../../../../../../../services/config';

// Import Helpers
import { findParentScopeById, getImageColors, getShade, checkDifferentLibraryShade } from "../../../../../../../helpers/helper";

// Import CSS
import './AddActionModal.css';

const defaultLibraryImg = `default_library.png`;

// this is shown when pressing the + icon from the modal flow screen
// There are 3 phases in this modal. These stages are kept in the name field in categories. These are 'module,library,action'
const AddActionModal = React.memo(({ selectAction, action, close, direction, isEhFlow }) => {
    const modules = useRecoilValue(automModules);
    const [categories, setCategories] = useState({ name: '', list: [], id: '' });
    const [scopeLibrary, setScopeLibrary] = useRecoilState(scopeLibraryAtom);
    const setAutomDetail = useSetRecoilState(activeAutom);
    const [baseOrigin, setBaseOrigin] = useState("");

    const [inputValue, setInputValue] = useState('');
    const [selectedValues, setSelectedValues] = useState([]);

    const { t } = useTranslation();
    const label = "CONTENT.FLOW.LEFT-AREA.";

    useEffect(() => {
        const init = async () => {
            const baseOr = await BASE_ORIGIN;
            setBaseOrigin(baseOr);
            let copyModules = JSON.parse(JSON.stringify(modules));
            let items

            // Data from recoile state is passed to hooks
            setAutomDetail((autom) => {
                items = JSON.parse(JSON.stringify(autom.items))
                return autom
            })
            let parentScope = await findParentScopeById(action.item_id, items, true)
            //Check for add scope button and scope rules
            for (let i = 0; i < copyModules.length; i++) {
                const libraries = copyModules[i].libraries;
                for (let j = 0; j < libraries.length; j++) {
                    const actions = await libraries[j].library_versions[libraries[j].library_versions.length - 1].actions;
                    let scopeLibrary
                    if (libraries[j].library_label === "DESKTOPACTIONS") {
                        let img = libraries[j].image?.includes('data:') ? libraries[j].image :
                            `${baseOr}/libraries/${!libraries[j].image || libraries[j].is_system ? "system" : "custom"}/${libraries[j].image ? libraries[j].image : defaultLibraryImg}`;
                        scopeLibrary = {
                            name: 'action', list: actions,
                            library: {
                                img,
                                library_label: libraries[j].library_label,
                                library_name: libraries[j].library_name,
                                version: libraries[j].library_versions[libraries[j].library_versions.length - 1].version,
                                versionId: libraries[j].library_versions[libraries[j].library_versions.length - 1].id,
                                application_id: libraries[j].application_id
                            }
                        }
                    }
                    copyModules[i].libraries[j].library_versions[libraries[j].library_versions.length - 1].actions = await actions.filter((item) => {
                        if (item.action_name === "SCOPE") {
                            setScopeLibrary({ ...scopeLibrary, action: item })
                        }
                        if (parentScope.actionName || (action.actionName === "SCOPE" && direction === "")) {
                            setScopeLibrary({ ...scopeLibrary, action: undefined })
                            return item.action_name !== "SCOPE"
                        }
                        if (checkEnabledActions(libraries[j], item)) {
                            return false
                        }
                        else {
                            if (libraries[j].library_label === "DESKTOPACTIONS") {
                                // setScopeLibrary({ ...scopeLibrary, action })
                                return item.action_name === "SCOPE"
                            }
                            return true
                        }
                    });
                }
            }
            setCategories({ name: 'module', list: copyModules });
        }
        init();

    }, [])

    const checkEnabledActions = (library, action) => {
        if ((library.library_name === "PDF" && ["EXPORT-TO-EXCEL"].includes(action.action_name)) ||
            (isEhFlow && library.library_name === "ADVANCED-ACTIONS" && ["RUN-COMMAND"].includes(action.action_name)) ||
            (library.library_name === "CONDITION" && ["ELSEIF", "ELSE"].includes(action.action_name))) {
            return true
        }
        return false
    }

    const getIconByModuleName = (category) => {
        if (categories.name === 'module') {
            switch (category.module_name) {
                case 'APPS':
                    return IntegrationIcon;
                case 'ADVANCED-ACTIONS': // Transform
                    return TransformIcon;
                case 'AI-ACTIONS':
                    return AIicon;
                case 'UI-ACTIONS':
                    return UIicon
                case 'UTILITIES':
                    return UtilityIcon
                default:
                    break;
            }
        } else if (categories.name === 'library') {
            return category.image?.includes('data:') ? category.image :
                `${baseOrigin}/libraries/${!category.image || category.is_system ? "system" : "custom"}/${category.image ? category.image : defaultLibraryImg}`
        } else {
            return categories.library?.img;
        }

    }

    const getTitle = (category) => {
        if (categories.name === 'module') {
            return t("CONTENT.AUTOMS-LIST." + category.module_name)
        } else if (categories.name === 'library') {
            return category.library_label.includes('CUSTOM') ? category.library_name :
                t("CONTENT.LIBRARY." + category.library_name + ".NAME");
        } else if (categories.name === 'action') {
            return categories.library?.library_label?.includes('CUSTOM') ? category?.action_name :
                t(`CONTENT.LIBRARY.${categories.library?.library_name}.ACTION.${category.action_name}`);
        }
    }

    const getDescription = (category) => {
        if (categories.name === 'module') {
            return t(label + category.module_name + "-DESC")
        } else {
            return "";
        }
    }

    // While checking the number, filtering is done based on the search criteria.
    const getChildrenCount = (category) => {
        if (categories.name === 'module') {
            return category.libraries ? category.libraries.filter(x => x.visible).length : 0
        } else if (categories.name === 'library') {
            return category.library_versions[category.library_versions.length - 1].actions
                ?
                category.library_versions[category.library_versions.length - 1].actions.filter(x => x.visible).length
                : 0
        } else {
            return undefined;
        }
    }

    // this function works when clicking on a module , library or action
    const selectCategory = (category, categories) => {
        // clicks are added to the search part in non-action clicks
        if (categories.name !== 'action') {
            let name = getTitle(category);
            setSelectedValues([...selectedValues, { name, id: category.id }]);
        }
        if (categories.name === 'module') {
            setCategories({ name: 'library', list: category.libraries })
        } else if (categories.name === 'library') {
            let actions = category.library_versions[category.library_versions.length - 1].actions ?
                category.library_versions[category.library_versions.length - 1].actions :
                [];
            let img = category.image?.includes('data:') ? category.image :
                `${baseOrigin}/libraries/${!category.image || category.is_system ? "system" : "custom"}/${category.image ? category.image : defaultLibraryImg}`;
            setCategories({
                name: 'action', list: actions,
                library: {
                    img,
                    library_label: category.library_label,
                    library_name: category.library_name,
                    version: category.library_versions[category.library_versions.length - 1].version,
                    versionId: category.library_versions[category.library_versions.length - 1].id,
                    application_id: category.application_id
                }
            })
        } else {
            getImageColors(categories.library.img, (result) => {
                let shade = getShade(result.background, 75);
                let isUniqe = checkDifferentLibraryShade(categories.library.library_name)
                shade = isUniqe.shade ? isUniqe.shade : shade;
                let new_action_detail = {
                    action_name: category.action_name,
                    action_label: category.action_label,
                    id: uuidv4(),
                    item_id: uuidv4(),
                    flow_behavior: category.flow_behavior,
                    parent_id: category.parent_id,
                    status: true,
                    order: null,
                    visible: true,
                    libraryLabel: categories.library.library_label,
                    libraryName: categories.library.library_name,
                    application_id: categories.library.application_id,
                    actionName: category.action_name,
                    library_label: categories.library.library_label,
                    library_name: categories.library.library_name,
                    image: categories.library.img,
                    shade,
                    fields: {},
                    isDebugging: false,
                    debugStatus: "",
                    debugErrorMessage: "",
                    version: categories.library.version,
                    versionId: categories.library.versionId,
                    actionId: category.id
                }
                // Since drag and drop operation is carried out manually, data is added to localStorage manually.
                localStorage.setItem("dragItem", JSON.stringify({ item: new_action_detail, isMove: false }));

                selectAction();
            })


        }

    }

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        filterBySearch(e.target.value, categories)
    };

    // It is used to return to the parent when an element in search is deleted.
    const findParentById = (id) => {
        for (const item of modules) {
            if (item.id === id) {
                return { name: 'module', list: modules }
            }
            if (item.libraries) {
                for (const lib of item.libraries) {
                    if (lib.id === id) {
                        return { name: 'library', list: item.libraries }
                    }
                }
            }
        }
        return { name: 'module', list: modules };
    }

    const handleRemoveValue = (index, id) => {
        let v = findParentById(id);
        v.list = JSON.parse(JSON.stringify(v.list))
        if (v.name === 'module') {
            setSelectedValues([]);
        } else {
            const updatedValues = [...selectedValues];
            updatedValues.splice(index, 1);
            setSelectedValues(updatedValues);
        }
        filterBySearch(inputValue, v)
    };

    const filterBySearch = (searchTextOrj, data) => {
        let copyCategories = JSON.parse(JSON.stringify(data));
        let searchText = searchTextOrj.toLowerCase();

        for (const item of copyCategories.list) {
            if (data.name === 'module') {
                item.visible = false;
                item.libraries.forEach(library => {
                    if (library.library_versions[library.library_versions.length - 1].actions) {
                        if (searchText === "") {
                            item.visible = true;
                            library.visible = true;
                            library.library_versions[library.library_versions.length - 1].actions.forEach(action => action.visible = true)
                        } else if (t("CONTENT.LIBRARY." + library.library_name + ".NAME")
                            .toLowerCase().includes(searchText)) {
                            item.visible = true;
                            library.visible = true;
                            library.library_versions[library.library_versions.length - 1].actions.forEach(action => action.visible = true)
                        } else {
                            library.visible = false;
                            library.library_versions[library.library_versions.length - 1].actions.forEach(action => {
                                if (t(`CONTENT.LIBRARY.${library.library_name}.ACTION.${action.action_name}`).toLowerCase().includes(searchText)) {
                                    item.visible = true;
                                    library.visible = true;
                                    action.visible = true;
                                } else action.visible = false;
                            })
                        }
                    }
                })
            } else if (data.name === 'library') {
                if (item.library_versions[item.library_versions.length - 1].actions) {
                    if (searchText === "") {
                        item.visible = true;
                        item.library_versions[item.library_versions.length - 1].actions.forEach(action => action.visible = true)
                    } else if (t("CONTENT.LIBRARY." + item.library_name + ".NAME")
                        .toLowerCase().includes(searchText)) {
                        item.visible = true;
                        item.library_versions[item.library_versions.length - 1].actions.forEach(action => action.visible = true)
                    } else {
                        item.visible = false;
                        item.library_versions[item.library_versions.length - 1].actions.forEach(action => {
                            if (t(`CONTENT.LIBRARY.${item.library_name}.ACTION.${action.action_name}`).toLowerCase().includes(searchText)) {
                                item.visible = true;
                                action.visible = true;
                            } else action.visible = false;
                        })
                    }
                }
            } else {
                if (t(`CONTENT.LIBRARY.${data.library.library_name}.ACTION.${item.action_name}`).toLowerCase().includes(searchText)) {
                    item.visible = true;
                } else item.visible = false;
            }
        }

        setCategories(copyCategories)
    }

    return (
        <Dialog maxWidth="sm" style={{ minWidth: '444px', userSelect: "none" }} open onClose={close} draggable="false">
            <DialogTitle>
                <div className="module-item-search h-38">
                    <img className="module-item-search-icon m-r-5" src={SearchIcon} alt="" draggable={false} />

                    {selectedValues.map((value, index) => (
                        <div key={index} className="selected-value">
                            {value.name}
                            <span className="remove-icon" onClick={() => handleRemoveValue(index, value.id)}>
                                x
                            </span>
                        </div>
                    ))}
                    <input
                        draggable={false}
                        className="module-item-search-text module-search-text search-input-text"
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                    />

                </div>
            </DialogTitle>
            <DialogContent className='add-action-content'>
                <div className={`action-modal-container module-col-${categories.name === 'module' ? '2' : '1'} `}>
                    {
                        categories.list.map((category, index) => (

                            (category.visible === undefined || category.visible) &&
                            <div onClick={() => selectCategory(category, categories)} className='module-container' key={category.id}>
                                <ModuleItem fromItem icon={getIconByModuleName(category)} title={getTitle(category)}
                                    description={getDescription(category)}
                                    count={getChildrenCount(category)}>

                                </ModuleItem>
                            </div>

                        ))
                    }
                </div>
                {categories.name === "module" && scopeLibrary.action && <div onClick={() => selectCategory(scopeLibrary.action, scopeLibrary)} className='module-container add-scope-button'>
                    <ModuleItem fromItem icon={scopeLibrary.library.img} title={"ADD SCOPE"}></ModuleItem>
                </div>}
            </DialogContent>
        </Dialog>
    )
})

export default AddActionModal;